import { Box, Button, IconButton, Palette, Stack, Tooltip, Typography } from "@mui/material";
import { History, Maximize2, Minimize2, Plus, X } from "lucide-react";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { borderGeneralStyle } from "@utils/cssStyles";
import { closePanel } from "@redux/slices/askDostChat";
import { useDispatch } from "@redux/store";
import { LoadingButton } from "@mui/lab";
import { AskDostHistoryMenu } from "./history/AskDostHistoryMenu";

interface IAskDostHeaderProps {
  isFullWidth: boolean;
  onExpandClick: () => void;
  palette: Palette;
  translate: (text: string, options?: any) => string;
  clearHistory: () => void;
  getNavigationState?: () => Record<string, any>;
  onSelectConversation?: (conversationId: string, history?: any) => void;
  onClose?: () => void;
  isHistoryPanelOpen?: boolean;
  onToggleHistoryPanel?: () => void;
  isResetting: boolean;
}

export const AskDostHeader = ({
  isFullWidth,
  onExpandClick,
  palette,
  translate,
  clearHistory,
  getNavigationState,
  onSelectConversation,
  onClose,
  isResetting,
  isHistoryPanelOpen,
  onToggleHistoryPanel,
}: IAskDostHeaderProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLElement | null>(null);

  const activeConversationTitle = useSelector((state: any) => state.askDostChat.activeConversationTitle);

  const handleTitleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isFullWidth) {
      onToggleHistoryPanel?.();
    } else {
      setHistoryAnchorEl(e.currentTarget);
    }
  };

  const handleExpandClick = () => {
    if (isFullWidth) {
      onExpandClick();
    } else {
      dispatch(closePanel());
      navigate("/dashboard/ask-dost", {
        state: { from: location.pathname, ...getNavigationState?.() },
      });
    }
  };

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else if (isFullWidth) {
      navigate(-1); 
    }
  };

  return (
    <Box
      sx={{
        px: { xs: 2, sm: 3 },
        py: isFullWidth ? 2 : "12px",
        width: "100%", 
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: "sticky",
        top: 0,
        zIndex: 1,
        backgroundColor: "transparent",
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        onClick={handleTitleClick}
        sx={{
          alignItems: "center",
          cursor: "pointer",
          borderRadius: 2,
          px: 1,
          ml: -1,
          minWidth: 0,
          "&:hover": { backgroundColor: palette.text.input },
        }}
      >
         <History
          size={16}
          color={
            isFullWidth && isHistoryPanelOpen
              ? palette.primary.main
              : palette.text.label
          }
          style={{ flexShrink: 0 }}
        />
        <Typography
          variant="subtitle1"
          noWrap
          sx={{ fontSize: "16px", fontWeight: 600, color: palette.text.heading }}
        >
          {activeConversationTitle || "Ask Dost"}
        </Typography>
      </Stack>

      {!isFullWidth && (
        <AskDostHistoryMenu
          anchorEl={historyAnchorEl}
          onClose={() => setHistoryAnchorEl(null)}
          palette={palette}
          translate={translate}
          onSelectConversation={onSelectConversation}
        />
      )}

      <Stack direction="row" spacing={1} alignItems="center">
        <LoadingButton
          size="small"
          variant="outlined"
          loadingPosition="start"
          onClick={clearHistory}
          loading={isResetting}
          startIcon={<Plus size={18} color={palette.primary.main} />}
          sx={{
            borderColor: palette.primary.main,
            color: palette.primary.main,
            textTransform: "none",
            whiteSpace: "nowrap",
          }}
        >
          {translate("new_chat")}
        </LoadingButton>

        <Tooltip title={translate(isFullWidth ? "collapse" : "expand")}>
          <IconButton
            size="small"
            onClick={handleExpandClick}
            sx={borderGeneralStyle}
          >
            {!isFullWidth ? (
              <Maximize2 size={16} color={palette.text.secondary} />
            ) : (
              <Minimize2 size={16} color={palette.text.secondary} />
            )}
          </IconButton>
        </Tooltip>

        {!isFullWidth && (
          <Tooltip title={translate("close")}>
            <IconButton
              size="small"
              onClick={handleClose}
              sx={borderGeneralStyle}
            >
              <X size={16} color={palette.text.secondary} />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};